import { hlsFetchSetup } from '@lib/hls/fetchSetup';
import { isHlsSupported } from '@lib/hls/support';
import { HLS_PLAYLIST_PATH, previewPathToUrl } from '@lib/network/preview';
import { libraryPreview } from '@stores/Library/LibraryPreview';
import Hls from 'hls.js';
import {
  type CachedFrame,
  computeCaptureSize,
  createFrameCache,
  DEFAULT_CAPTURE_QUALITY,
  frameCacheKey,
  type FrameCaptureOptions,
  frameVariant,
  mediaKeyOf,
  pickNextJob,
  quantiseTimestamp,
} from './frameCaptureCore';

export type { FrameCaptureOptions } from './frameCaptureCore';

/**
 * Frame grabs taken client-side from a media's preview HLS stream — the same
 * source Search moment cards play.
 *
 * One hidden <video> + Hls instance is kept alive per media and pending
 * timestamps are walked in playback order, so consecutive frames resolve out of
 * already-buffered segments in tens of milliseconds. The alternative, which this
 * replaced, was building and destroying a whole Hls instance per frame and
 * re-downloading segments each time.
 *
 * The player is released when the last surface drops its lease
 * ({@link acquireFrameCapture}) or after a spell with nothing to do. Cached
 * frames outlive it — they're byte-bounded, so keeping them makes a return trip
 * instant at no lasting cost.
 *
 * Pure decisions (sizing, keys, scheduling, the cache bound) live in
 * `frameCaptureCore.ts`.
 */

/** Thrown when the browser can't decode HLS at all, so no capture is possible. */
export class FrameCaptureUnsupportedError extends Error {
  constructor() {
    super('Frame capture requires MSE (HLS.js is unsupported on this browser)');
    this.name = 'FrameCaptureUnsupportedError';
  }
}

/**
 * Thrown when every requester of a frame aborted mid-capture. Distinct from a
 * real failure because the pipeline is fine — tearing the player down here would
 * throw away exactly the warm state the next frame needs.
 */
class FrameCaptureAbandonedError extends Error {
  constructor() {
    super('Frame capture: abandoned by all requesters');
    this.name = 'FrameCaptureAbandonedError';
  }
}

type Settler = {
  resolve: (objectUrl: string) => void;
  reject: (error: unknown) => void;
  signal?: AbortSignal;
};

type FrameJob = {
  key: string;
  libraryId: string;
  mediaId: string;
  timestamp: number;
  options?: FrameCaptureOptions;
  settlers: Settler[];
};

const CAPTURE_TIMEOUT_MS = 15_000;
const IDLE_TEARDOWN_MS = 30_000;

const frameCache = createFrameCache();
const jobs = new Map<string, FrameJob>();
let pumping = false;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let leaseCount = 0;
// Bumped by destroyPlayer so an ensurePlayer stalled across an await (e.g. a
// slow token fetch outliving the job timeout) can tell it was superseded and
// must not install its player over a newer one.
let playerGeneration = 0;

type Player = {
  mediaKey: string;
  video: HTMLVideoElement;
  canvas: HTMLCanvasElement;
  hls: Hls;
  ready: Promise<void>;
};

let player: Player | null = null;

/**
 * Whether the app should offer client-side capture.
 *
 * Goes through `isHlsSupported()` rather than `Hls.isSupported()` so the debug
 * "Mock HLS.js unsupported" toggle takes effect here too — callers use this to
 * decide whether to render a fallback, and that decision has to be testable
 * without a real pre-MSE device.
 */
export const isFrameCaptureSupported = () => isHlsSupported();

/**
 * Register interest in the hidden capture player, and get back a release
 * function. The player is torn down when the last lease is released, so a
 * surface that navigates away frees the video without waiting out the idle
 * timer.
 *
 * Acquire from a mount effect and release from its cleanup — never from a
 * constructor or a `useState` initialiser, which have no matching teardown. The
 * lease is an optimisation, not a gate: capture still works without one, it
 * just leans on the idle timer to clean up.
 */
export function acquireFrameCapture(): () => void {
  leaseCount += 1;
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    leaseCount = Math.max(0, leaseCount - 1);
    if (leaseCount === 0 && !pumping) {
      // Release the player, and *only* the player. Rejecting pending jobs here
      // would reach work this lease never owned: leases are held by the moment
      // timeline and the atlas page, while `MomentThumbnail` and `TimedThumb` take
      // none — so closing a leased surface used to cancel unleased consumers'
      // in-flight frames. Any job left in the queue simply rebuilds the player on
      // the next pump.
      //
      // Skipped while pumping for the same reason: tearing the video out from
      // under an in-flight `waitForSeek` means its `seeked` never arrives, so that
      // consumer waits out the full capture timeout and then fails, rather than
      // just losing the player. `pump` schedules an idle teardown when it drains,
      // which collects the player a moment later without that cost.
      destroyPlayer();
    }
  };
}

/**
 * Synchronous read of an already-captured frame, as an object URL.
 *
 * Reading promotes the frame's recency, which is load-bearing: callers that read
 * through this on every render keep whatever is on screen at the young end of
 * the cache, so eviction (which revokes the Blob) only ever reaches frames
 * nobody is displaying. Never copy the returned URL into a longer-lived store —
 * this cache owns the Blob's lifetime.
 */
export function peekVideoFrame(
  libraryId: string,
  mediaId: string,
  timestamp: number,
  options?: FrameCaptureOptions,
): string | null {
  return frameCache.get(frameCacheKey(libraryId, mediaId, timestamp, options))?.url ?? null;
}

/**
 * Every frame captured so far for one media at one size, ordered by timestamp.
 * Lets a caller find the nearest available frame to stand in for one still
 * loading, without holding a second copy of the cache.
 */
export function listCapturedFrames(
  libraryId: string,
  mediaId: string,
  options?: FrameCaptureOptions,
): { timestamp: number; url: string }[] {
  const mediaKey = mediaKeyOf(libraryId, mediaId);
  const variant = frameVariant(options);
  const frames: { timestamp: number; url: string }[] = [];
  for (const frame of frameCache.values()) {
    if (frame.mediaKey === mediaKey && frame.variant === variant) {
      frames.push({ timestamp: frame.timestamp, url: frame.url });
    }
  }
  frames.sort((a, b) => a.timestamp - b.timestamp);
  return frames;
}

/** Resolve a frame at `timestamp` (seconds) as an object URL over a JPEG Blob. */
export function getVideoFrame(
  libraryId: string,
  mediaId: string,
  timestamp: number,
  options?: FrameCaptureOptions,
  signal?: AbortSignal,
): Promise<string> {
  const key = frameCacheKey(libraryId, mediaId, timestamp, options);
  const cached = frameCache.get(key);
  if (cached) {
    return Promise.resolve(cached.url);
  }
  if (!Hls.isSupported()) {
    return Promise.reject(new FrameCaptureUnsupportedError());
  }
  if (signal?.aborted) {
    return Promise.reject(new DOMException('Aborted', 'AbortError'));
  }
  return new Promise<string>((resolve, reject) => {
    let job = jobs.get(key);
    if (!job) {
      job = {
        key,
        libraryId,
        mediaId,
        timestamp: quantiseTimestamp(timestamp, options?.timestampQuantum),
        options,
        settlers: [],
      };
      jobs.set(key, job);
    }
    job.settlers.push({ resolve, reject, signal });
    void pump();
  });
}

// ---------------------------------------------------------------- internals

function scheduleIdleTeardown() {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    idleTimer = null;
    if (jobs.size === 0 && !pumping) destroyPlayer();
  }, IDLE_TEARDOWN_MS);
}

function destroyPlayer() {
  playerGeneration += 1;
  if (!player) return;
  const { hls, video, canvas } = player;
  player = null;
  hls.destroy();
  video.remove();
  canvas.remove();
}

async function ensurePlayer(libraryId: string, mediaId: string, startAt: number): Promise<Player> {
  const mediaKey = mediaKeyOf(libraryId, mediaId);
  if (player?.mediaKey === mediaKey) {
    return player;
  }
  destroyPlayer();
  const generation = playerGeneration;

  const token = await libraryPreview.getValidPreviewToken(libraryId);
  if (generation !== playerGeneration) {
    // While the token fetch was in flight, the job timed out (or the surface
    // tore down) and a newer player may already be seeking — installing ours
    // over it would orphan a live HLS instance.
    throw new Error('Frame capture: superseded during setup');
  }
  if (!token) {
    throw new Error(`Frame capture: no preview token for library ${libraryId}`);
  }
  const url = previewPathToUrl(libraryId, mediaId, HLS_PLAYLIST_PATH);

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  // Load-bearing only if a native-HLS fallback is ever added: MSE-sourced
  // frames don't taint the canvas, but a cross-origin video.src would.
  video.crossOrigin = 'anonymous';
  video.style.display = 'none';
  document.body.appendChild(video);

  const canvas = document.createElement('canvas');
  canvas.style.display = 'none';
  document.body.appendChild(canvas);

  const hls = new Hls({
    // A getter, not the captured string: this player outlives a single frame, so
    // by the time it makes its hundredth request the token it was built with may
    // have rotated. Falling back to that token keeps behaviour no worse than a
    // static one when the cache has nothing fresher. If it does go stale, the
    // resulting fatal error tears the player down (see `pump`) and the rebuild
    // goes through `getValidPreviewToken`, which refreshes.
    fetchSetup: hlsFetchSetup(() => libraryPreview.getCachedPreviewToken(libraryId) ?? token),
    // Load-bearing for AUTH, not performance — do not remove. See the warning on
    // `hlsFetchSetup`, which explains why.
    progressive: true,
    // Keep this instance's appetite small. It is not the player the user is
    // watching — it now runs *alongside* that one on the same media, so an
    // unbounded buffer here competes for exactly the bandwidth that makes
    // playback stutter. Independent of the loader choice above.
    maxBufferLength: 3,
    maxMaxBufferLength: 30,
    // Lowest rendition first: these frames are small, speed wins over fidelity.
    startLevel: 0,
    startPosition: startAt,
  });

  const ready = new Promise<void>((resolve, reject) => {
    const onError = (_: unknown, data: { fatal?: boolean; type?: string }) => {
      if (data.fatal) reject(new Error(`Frame capture: HLS error (${data.type})`));
    };
    hls.on(Hls.Events.ERROR, onError);
    hls.once(Hls.Events.MANIFEST_PARSED, () => resolve());
  });

  hls.loadSource(url);
  hls.attachMedia(video);

  const created: Player = { mediaKey, video, canvas, hls, ready };
  player = created;
  return created;
}

function waitForSeek(video: HTMLVideoElement): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timeout);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
    };
    const onSeeked = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('Frame capture: video error while seeking'));
    };
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Frame capture: seek timed out'));
    }, CAPTURE_TIMEOUT_MS);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
  });
}

/**
 * Give the decoder a beat after `seeked` before drawing. Deliberately NOT
 * requestVideoFrameCallback: a paused, hidden video never *presents* a frame,
 * so rVFC would wait forever — that exact mistake once wedged the capture queue
 * on the first job and left every thumbnail on its shimmer. After `seeked` the
 * frame at currentTime is already drawable.
 *
 * The timeout backstop matters: rAF is suspended entirely in hidden tabs, and
 * without it every capture queued while the user is on another tab would stall
 * into the hard job timeout, tearing down a healthy pipeline each time.
 */
function waitForFrame(): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, 150);
    requestAnimationFrame(() => requestAnimationFrame(done));
  });
}

/**
 * `toBlob` is the cheap path — it hands back the encoded bytes without ever
 * building a base64 string. It can hand back null, so fall back to `toDataURL`
 * and decode that into the same Blob shape rather than special-casing callers.
 */
function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        try {
          resolve(dataUrlToBlob(canvas.toDataURL('image/jpeg', quality)));
        } catch (error) {
          reject(error);
        }
      },
      'image/jpeg',
      quality,
    );
  });
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, payload] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

async function captureJob(job: FrameJob, isAbandoned: () => boolean): Promise<Blob> {
  // Bail at every await boundary once nobody wants this frame any more.
  //
  // Scrolling or zooming the filmstrip abandons requests wholesale, and the pump
  // is serial — so without these checks an abandoned capture still pays for a
  // player build, a fragment fetch and a JPEG encode while the frames actually
  // on screen wait behind it. That is most of why the first pass over a strip
  // used to feel slow and later passes did not.
  const abandon = () => {
    if (isAbandoned()) throw new FrameCaptureAbandonedError();
  };

  const { video, canvas, ready } = await ensurePlayer(job.libraryId, job.mediaId, job.timestamp);
  await ready;
  abandon();

  const duration = Number.isFinite(video.duration) ? video.duration : Infinity;
  const target = Math.min(job.timestamp, Math.max(0, duration - 0.1));
  if (Math.abs(video.currentTime - target) > 0.05 || video.readyState < 2) {
    video.currentTime = target;
    await waitForSeek(video);
    abandon();
  }
  await waitForFrame();
  abandon();

  const { width, height } = computeCaptureSize({
    sourceWidth: video.videoWidth,
    sourceHeight: video.videoHeight,
    maxWidth: job.options?.maxWidth,
    maxHeight: job.options?.maxHeight,
  });
  if (!width || !height) {
    throw new Error('Frame capture: no decoded frame');
  }
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Frame capture: no canvas context');
  }
  ctx.drawImage(video, 0, 0, width, height);
  return canvasToJpegBlob(canvas, job.options?.quality ?? DEFAULT_CAPTURE_QUALITY);
}

async function pump() {
  if (pumping) return;
  pumping = true;
  try {
    for (;;) {
      const job = pickNextJob(jobs.values(), player?.mediaKey, player?.video.currentTime ?? 0);
      if (!job) break;
      jobs.delete(job.key);
      const live = job.settlers.filter((s) => !s.signal?.aborted);
      const aborted = job.settlers.filter((s) => s.signal?.aborted);
      for (const settler of aborted) {
        settler.reject(new DOMException('Aborted', 'AbortError'));
      }
      if (live.length === 0) continue;

      // Serve from cache if this frame arrived while we were busy.
      //
      // `jobs.delete` above happens before the await, so a request for the same
      // key during a capture opens a *second* job under that key. Capturing it
      // again would `frameCache.set` over the first entry — and the LRU revokes
      // the value it replaces, which by then is an object URL already handed to
      // settlers and rendered in an <img>. Re-checking here is what stops a
      // duplicate request from pulling the Blob out from under the first one.
      const alreadyCaptured = frameCache.get(job.key);
      if (alreadyCaptured) {
        for (const settler of live) settler.resolve(alreadyCaptured.url);
        continue;
      }

      // The queue is serial: one silently-hanging capture would starve every
      // pending frame, so the whole job races a hard timeout.
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      try {
        const blob = await Promise.race([
          captureJob(job, () => live.every((s) => s.signal?.aborted)),
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error('Frame capture: job timed out')),
              CAPTURE_TIMEOUT_MS,
            );
          }),
        ]);
        const entry: CachedFrame = {
          url: URL.createObjectURL(blob),
          bytes: blob.size,
          mediaKey: mediaKeyOf(job.libraryId, job.mediaId),
          variant: frameVariant(job.options),
          timestamp: job.timestamp,
        };
        frameCache.set(job.key, entry);
        for (const settler of live) settler.resolve(entry.url);
      } catch (error) {
        if (error instanceof FrameCaptureAbandonedError) {
          // Nobody is waiting and nothing is broken. Keep the warm player — it is
          // very likely positioned near whatever the user scrolled to, which is
          // what the next job wants.
          for (const settler of live) {
            settler.reject(new DOMException('Aborted', 'AbortError'));
          }
        } else {
          // A broken pipeline poisons every later seek on this media — rebuild.
          destroyPlayer();
          for (const settler of live) settler.reject(error);
        }
      } finally {
        clearTimeout(timeoutId);
      }
    }
  } finally {
    pumping = false;
    scheduleIdleTeardown();
  }
}
